/**
 * StatusPillBubble (Phase 31 Chunk H)
 *
 * Small pill-shaped bubble shown below the last message in CoachPage while the
 * Gemini agent is mid-tool-call. Renders as a compact Guild Mint pill with
 * Guild Green text and 3 animated dots prefix (mirrors `TypingIndicator`
 * pattern from CoachPage.tsx but inline + with a status string).
 *
 * Pure presentational: state, debounce, network-throttle fallback, and
 * clear-on-token logic all live in the consumer (CoachPage chunk I).
 *
 * Reuses the `coachBounce` keyframe and `coach-msg-appear` class declared
 * inline in `CoachPage.tsx` — both already gated on
 * `prefers-reduced-motion: no-preference`, so this component inherits
 * reduced-motion support for free.
 */

interface StatusPillBubbleProps {
  /** Pill text, e.g. "Looking up your recent scores...". */
  status: string;
}

export function StatusPillBubble({ status }: StatusPillBubbleProps) {
  return (
    <div className="flex w-full justify-start coach-msg-appear">
      <div
        role="status"
        aria-live="polite"
        aria-label="Coach is working on something for you"
        className="inline-flex max-w-[80%] items-center gap-2 rounded-full bg-guild-mint px-3 py-1.5 ring-1 ring-guild-green/20"
      >
        <span className="flex gap-1" aria-hidden="true">
          <span
            className="h-1.5 w-1.5 rounded-full bg-guild-green"
            style={{ animation: 'coachBounce 1.2s infinite 0ms' }}
          />
          <span
            className="h-1.5 w-1.5 rounded-full bg-guild-green"
            style={{ animation: 'coachBounce 1.2s infinite 200ms' }}
          />
          <span
            className="h-1.5 w-1.5 rounded-full bg-guild-green"
            style={{ animation: 'coachBounce 1.2s infinite 400ms' }}
          />
        </span>
        <span className="text-sm font-medium text-guild-green">{status}</span>
      </div>
    </div>
  );
}
